// CategoryId.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import { SUPPORTED_LANGUAGES, CATEGORY_FEED_ALL } from '../RemoteConfigKeys.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';
import { generateCollectionPageJsonLd } from '../resource/generateCollectionPageJsonLd.js';
import { createInitialStoreWithRequest } from '../createInitialStore.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({ context, params, request }) => {
  const log = legacySSRLog.extend('CategoryId loader()');
  const errorLog = legacySSRError.extend('CategoryId loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Get category feeds from remote config
    let state = store.getState();
    const { categoryId } = params;
    const categoryFeeds = getRemoteConfigData(
      state,
      CATEGORY_FEED_ALL,
      undefined,
      {
        CATEGORY: categoryId,
      }
    );

    // Fetch all category feeds count
    let totalFeedCount = 0;
    if (categoryFeeds && categoryFeeds.length > 0) {
      const feedCountResults = await Promise.allSettled(
        categoryFeeds.map(categoryFeedName =>
          fetchFeedTotalCount({
            feedNameWithQuery: categoryFeedName,
            httpProxyHeaders,
          })
        )
      );

      // Sum up all feed counts
      totalFeedCount = feedCountResults.reduce((sum, result) => {
        return sum + (result.status === 'fulfilled' ? result.value : 0);
      }, 0);
    }

    state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData, totalFeedCount });

    // Get i18n instance
    const { i18n } = getI18nInstance();
    const { t } = i18n;

    // Get category name from translation
    const categoryName = t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
    });

    // Prepare title and description
    const title = t('seo_category_all_title', {
      ns: TranslationNamespace.SEO,
      category: categoryName,
    });
    const description =
      t(`seo_category_${categoryId}_all_description`, {
        ns: TranslationNamespace.SEO,
      }) ||
      t('seo_category_all_description', {
        ns: TranslationNamespace.SEO,
        category: categoryName,
      }) ||
      '';

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const canonicalPath = `/category/${categoryId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
    });

    // Generate JSON-LD
    const hostname = request?.headers?.get('host') || 'swag.live';
    const origin = `https://${hostname}`;

    const jsonLd = await generateCollectionPageJsonLd({
      origin,
      categoryId,
      state,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareCollectionPageMeta({
      state,
      title,
      description,
      keywords: [categoryName, 'SWAG', 'category'],
      hostname,
      canonical,
      alternates,
      jsonLd,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} }, // We don't need to pass store data for now
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname,
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const CategoryIdRoute = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'CategoryId',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalComponent
        filename="Container_MessageCategoryTabAllInOne"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/MessageCategoryTabAllInOne.js')
        }
      />
    </>
  );
};

export default CategoryIdRoute;
